"use client"

import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Button,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material"
import { CallboxPublicRequest, postCareerDriverOrCollegeApply } from "api"
import { useLocale, useTranslations } from "next-intl"
import Image from "next/image"
import { LegacyRef, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, useForm } from "react-hook-form"
import { MarketingConsent } from "ui"
import ActiveCheckbox from "ui/icons/ActiveCheckbox"
import ArrowDown from "ui/icons/ArrowDown"
import CheckboxIcon from "ui/icons/CheckboxIcon"
import { sharedColors } from "ui/theme/colors"
import {
  replaceRichTextLinks,
  sendCvClick,
  sendCvError,
  sendCvSuccess,
} from "utils"
import { generateSchema } from "utils/FormSchema"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import * as yup from "yup"
import { FormError } from "../ContactUs/ContactContent"
import {
  CustomSelect,
  CustomTextField,
  RECAPTCHA_KEY,
} from "../ContactUs/Forms/Common"
import Result from "./FormResult"

const OuterLayout = styled.div<{ designB?: boolean; designC?: boolean }>`
  padding: ${(props) => (props.designB || props.designC ? "0" : "0 16px")};
  z-index: 1;
  overflow-y: ${(props) => (props.designB || props.designC ? "unset" : "auto")};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0;
    height: ${(props) => (props.designB ? "unset" : "100%")};
  }
`

const InnerLayout = styled.div<{
  designB?: boolean
  designC?: boolean
  designD?: boolean
}>`
  display: flex;
  flex-direction: column;
  min-height: ${(props) => (props.designC ? "unset" : "617px")};
  width: 100%;
  overflow-y: ${(props) => (props.designC ? "visible" : "auto")};
  padding: ${(props) => (props.designB ? "48px 16px 32px" : "24px 16px")};
  border-radius: 12px;
  justify-content: center;
  background-color: white;
  box-shadow: ${(props) =>
    props.designB || props.designC
      ? "unset"
      : "0 12px 25px 0 rgba(38, 48, 61, 0.32)"};
  ${(props) =>
    (props.designC || props.designD) &&
    `border: 4px solid ${sharedColors.brandDark};`}
  ${(props) => props.designB && "text-align: center;"}
  ${(props) => props.theme.breakpoints.up("md")} {
    border-radius: ${(props) => (props.designC ? "12px" : "0")};
    height: 100%;
    padding: ${(props) =>
      props.designC
        ? "48px 24px"
        : props.designB
          ? "80px 64px"
          : "48px 40px 32px"};
    justify-content: flex-start;
  }
`

const InnerInnerLayout = styled.div<{ designB?: boolean; designC?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${(props) =>
    props.designB
      ? `
    text-align: center;
    `
      : ""}
  ${(props) => props.theme.breakpoints.up("md")} {
    ${(props) => props.designC && "height: 100%;"}
    gap: 32px;
  }
`

const FormLayout = styled.form<{ designB?: boolean; designC?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${(props) =>
    props.designB
      ? `
    align-items: center;
    `
      : ""}
  ${(props) => props.designC && "flex: 1;"}
`

const ErrorText = styled((props: React.ComponentProps<typeof Typography>) => (
  <Typography variant="body3" color="error" {...props} />
))`
  && {
    font-size: 14px;
    line-height: 22px;
  }
`

const RadioBox = styled.div<{ designC?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: ${(props) => (props.designC ? "0" : "24px")};
  ${(props) => props.designC && "position: relative;"}
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0;
  }
`

const StyledRadioGroup = styled(RadioGroup)<{ designB?: boolean }>`
  display: flex;
  gap: 24px;
  flex-direction: row;
  ${(props) =>
    props.designB
      ? `
    justify-content: center;
    `
      : ""}
`

const StyledFormControlLabel = styled(FormControlLabel, {
  shouldForwardProp: (prop) => prop !== "$designC",
})<{ $designC?: boolean }>`
  /* Focus styles for the entire label */
  &:focus-visible {
    outline: 2px solid black;
    outline-offset: 2px;
    border-radius: 4px;
  }

  /* Target the radio button container specifically */
  .MuiRadio-root:focus-visible {
    outline: 2px solid black !important;
    outline-offset: 2px !important;
    border-radius: 50% !important;
  }

  /* Label text styling */
  .MuiFormControlLabel-label {
    font-size: 18px;
    line-height: 26px;
  }

  ${(props) =>
    props.$designC
      ? `
    .MuiRadio-root {
      padding: 0 9px;
      color: ${sharedColors.borderGray};
    }
    .MuiRadio-root.Mui-checked {
      color: ${sharedColors.brandDark};
    }
  `
      : ""}
`

const StyledTypography = styled(Typography)`
  p {
    margin: 0;
  }
`

const StyledGrid = styled.div<{ designB?: boolean; designC?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.designC ? "36px" : "10px")};
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    ${(props) =>
      props.designB
        ? `
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    `
        : ""}
  }
`

const LegalTextContainer = styled.div<{ designC?: boolean }>`
  padding-top: ${(props) => (props.designC ? "16px" : "24px")};
  ${(props) => props.theme.breakpoints.up("md")} {
    ${(props) =>
      props.designC
        ? ""
        : `
    margin-top: 48px;
    padding-top: 40px;
    border-top: 1px solid rgba(209, 213, 219, 1);
    `}
  }

  width: 100%;
  display: flex;
  justify-content: center;
`

const TrimmedTypography = styled(Typography)`
  text-box-trim: trim-both;
  text-box-edge: cap alphabetic;
  > * {
    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
  && {
    font-weight: 400;
  }
`

const StyledCheckboxRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  margin-bottom: 44px;
`

const ButtonContainer = styled.div<{ designB?: boolean }>`
  padding-top: 24px;
  width: 100%;
  align-self: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 24px;
    max-width: ${(props) => (props.designB ? "300px" : "100%")};
  }
`

const generatedSchema = generateSchema("he")

export default function Form({
  formTitle,
  termsConfirmationText,
  designB,
  designC,
  designD,
  legalText,
  logo,
  tooltipText = "",
  areaList,
  buttonText,
  anchorId,
}: {
  formTitle: string
  termsConfirmationText?: string
  designB?: boolean
  designC?: boolean
  designD?: boolean
  legalText?: string
  logo?: any
  tooltipText?: string
  areaList?: string[]
  buttonText?: string
  anchorId?: string
}) {
  const [isFormError, setIsFormError] = useState<FormError>("notSubmitted")
  const t = useTranslations("Forms")
  const reCaptchaRef = useRef<ReCAPTCHA>(null)
  const [pending, setPending] = useState(false)
  const locale = useLocale()
  const designCD = designC || designD
  const schema = designD
    ? yup.object({
        firstName: yup
          .string()
          .required("יש להזין שם מלא")
          .min(2, "יש להזין שם מלא")
          .max(35, "שם יכול להכיל עד 35 תווים")
          .matches(/^[a-zA-Z\u05D0-\u05EA\s]+$/, "שם חייב להכיל אותיות בלבד"),
        phone: yup
          .string()
          .required("יש להזין מספר טלפון")
          .matches(/^\d{10}$/, "מספר טלפון לא תקין"),
        organization: yup
          .string()
          .required("יש להזין שם חברה")
          .min(2, "יש להזין שם חברה")
          .max(35, "שם חברה יכול להכיל עד 35 תווים")
          .matches(/^[a-zA-Z\u05D0-\u05EA\s]+$/, "שם חברה חייב להכיל אותיות בלבד"),
      })
    : designC
      ? yup.object({
          firstName: yup
            .string()
            .required("יש להזין שם מלא")
            .min(2, "יש להזין שם מלא")
            .max(35, "שם חייב להכיל אותיות בלבד")
            .matches(/^[a-zA-Z\u05D0-\u05EA\s]+$/, "שם חייב להכיל אותיות בלבד")
            .test("min-words", "נא להזין שם פרטי ושם משפחה", (value) => {
              if (!value) return true
              const words = value.trim().split(/\s+/).filter((w) => w.length > 0)
              return words.length >= 2
            }),
          phone: yup
            .string()
            .required("יש להזין מספר טלפון")
            .matches(/^\d{10}$/, "מספר טלפון לא תקין"),
          preferredWorkRegion: yup.string().required("יש לבחור אזור עבודה מועדף"),
          hasLicense: yup
            .boolean()
            .nullable()
            .oneOf([true, false], "יש לבחור האם קיים רישיון נהיגה")
            .required("יש לבחור האם קיים רישיון נהיגה"),
          complaint: yup.boolean().optional(),
        })
      : yup.object({
          firstName: generatedSchema.fields.fullname,
          phone: generatedSchema.fields.phonehe,
          preferredWorkRegion: yup.string().required("אנא בחר איזור עבודה מועדף"),
          hasLicense: yup.boolean(),
          complaint: yup.boolean().optional(),
        })
  type FormData = yup.InferType<typeof schema>

  const cmsOptions = areaList?.length
    ? areaList.map((area: string) => ({ value: area, label: area }))
    : null

  const options = cmsOptions || [
    { value: "חיפה", label: locale === "he" ? "חיפה" : "Haifa" },
    {
      value: "העמק (עפולה)",
      label: locale === "he" ? "העמק (עפולה)" : "The Valley (Afula)",
    },
    { value: "כרמיאל", label: locale === "he" ? "כרמיאל" : "Karmiel" },
    { value: "שרון", label: locale === "he" ? "שרון" : "Sharon" },
    { value: "מרכז", label: locale === "he" ? "מרכז" : "Central" },
    {
      value: "אשדוד-אשקלון",
      label: locale === "he" ? "אשדוד-אשקלון" : "Ashdod–Ashkelon",
    },
    { value: "דרום", label: locale === "he" ? "דרום" : "South" },
    {
      value: "ירושלים רבתית",
      label: locale === "he" ? "ירושלים רבתית" : "Greater Jerusalem",
    },
    { value: "אילת", label: locale === "he" ? "אילת" : "Eilat" },
  ]
  const {
    handleSubmit,
    control,
    setValue,
    register,
    reset,
    formState: { errors, isSubmitted, isValid, isSubmitting },
  } = useForm<FormData>({
    defaultValues: designD
      ? { firstName: "", phone: "", organization: "" }
      : {
          firstName: "",
          phone: "",
          preferredWorkRegion: "",
          hasLicense: designC ? (null as unknown as boolean) : false,
        },
    resolver: yupResolver(schema as any) as any,
    mode: "onChange",
  })
  const resetForm = () => {
    setIsFormError("notSubmitted")
    reset()
  }
  const onSubmit = async (data: FormData) => {
    sendCvClick(
      t("submitCV") || "",
      "ללמוד במכללת אגד",
      "תחבורה ציבורית",
      "LandingPage",
    )
    const gRecaptchaResponse =
      (await reCaptchaRef?.current?.executeAsync()) as string
    reCaptchaRef?.current?.reset()
    setPending(true)
    try {
      const formdata = designD
        ? {
            // Landing Page 4 (designD) has no license field — omit hasLicense
            // from the payload instead of sending a misleading default of false.
            fullName: data.firstName as string,
            cellular: data.phone as string,
            organization: (data as any).organization as string,
            address: "",
            complaint: false,
            language: "he",
            sid: window?.__ctm?.config?.sid || "-1",
            recaptchaResponse: gRecaptchaResponse,
            applyFormType: "Driver" as const,
          }
        : {
            fullName: data.firstName as string,
            cellular: data.phone as string,
            address: (data as any).preferredWorkRegion,
            hasLicense: (data as any).hasLicense || false,
            complaint: (data as any).complaint || false,
            organization: "",
            language: "he",
            sid: window?.__ctm?.config?.sid || "-1",
            recaptchaResponse: gRecaptchaResponse,
            applyFormType: "Driver" as const,
          }
      const response = await postCareerDriverOrCollegeApply(
        formdata as CallboxPublicRequest,
      )
      if (response && response.data.body) {
        setIsFormError("no")
        sendCvSuccess(
          t("submitCV") || "",
          "ללמוד במכללת אגד",
          "תחבורה ציבורית",
          "Landing",
        )
      } else {
        setIsFormError("yes")
        sendCvError(
          t("submitCV") || "",
          "ללמוד במכללת אגד",
          "תחבורה ציבורית",
          "Landing",
        )
      }
    } catch (error) {
      sendCvError(
        t("submitCV") || "",
        "ללמוד במכללת אגד",
        "תחבורה ציבורית",
        "Landing",
      )
      setIsFormError("yes")
    } finally {
      setPending(false)
    }
  }
  return (
    <OuterLayout id={anchorId || "form"} designB={designB} designC={designCD}>
      <InnerLayout designB={designB} designC={designCD} designD={designD}>
        {isFormError === "notSubmitted" ? (
          <>
            <InnerInnerLayout designB={designB} designC={designCD}>
              {logo && (
                <Image
                  src={getUmbracoMediaUrl(logo)}
                  alt=""
                  width={80}
                  height={80}
                  style={{ alignSelf: "center" }}
                />
              )}
              {designCD ? (
                <TrimmedTypography
                  variant="h3"
                  component="div"
                  dangerouslySetInnerHTML={{
                    __html: replaceRichTextLinks(formTitle),
                  }}
                />
              ) : (
                <TrimmedTypography variant="h3">{formTitle}</TrimmedTypography>
              )}
              <TrimmedTypography variant="body2">
                שדה חובה<span style={{ color: "#E00700" }}>*</span>
              </TrimmedTypography>
              <FormLayout
                onSubmit={handleSubmit(onSubmit)}
                designB={designB}
                designC={designCD}
                aria-label={formTitle}
              >
                <StyledGrid designB={designB} designC={designCD}>
                  <CustomTextField
                    name="firstName"
                    control={control}
                    label={t("fullName")}
                    required={true}
                    isSubmitted={isSubmitted}
                    errors={errors}
                    fontSize={16}
                    {...(designCD && {
                      renderHelperText: (error, message) =>
                        error ? <ErrorText>{message}</ErrorText> : null,
                      sx: {
                        marginBlockEnd: 0,
                        position: "relative",
                        "& .MuiFormHelperText-root": {
                          position: "absolute",
                          top: "100%",
                          margin: 0,
                        },
                      },
                    })}
                  />
                  <CustomTextField
                    name="phone"
                    control={control}
                    label={t("mobileNumber")}
                    required={true}
                    isSubmitted={isSubmitted}
                    errors={errors}
                    fontSize={16}
                    {...(designCD && {
                      renderHelperText: (error, message) =>
                        error ? <ErrorText>{message}</ErrorText> : null,
                      sx: {
                        marginBlockEnd: 0,
                        position: "relative",
                        "& .MuiFormHelperText-root": {
                          position: "absolute",
                          top: "100%",
                          margin: 0,
                        },
                      },
                    })}
                  />
                  {designD && (
                    <CustomTextField
                      name="organization"
                      control={control}
                      label="שם חברה"
                      required={true}
                      isSubmitted={isSubmitted}
                      errors={errors}
                      fontSize={16}
                      renderHelperText={(error, message) =>
                        error ? <ErrorText>{message}</ErrorText> : null
                      }
                      sx={{
                        marginBlockEnd: 0,
                        position: "relative",
                        "& .MuiFormHelperText-root": {
                          position: "absolute",
                          top: "100%",
                          margin: 0,
                        },
                      }}
                    />
                  )}
                  {!designD && (<>
                  <CustomSelect
                    control={control}
                    label={
                      locale === "he"
                        ? "אזור עבודה מועדף"
                        : "Preferred work region"
                    }
                    name="preferredWorkRegion"
                    options={options}
                    required={true}
                    errors={errors}
                    fontSize={16}
                    isSubmitted={isSubmitted}
                    IconComponent={ArrowDown}
                    sx={{
                      "& .MuiSelect-icon": {
                        width: "24px !important",
                        insetInlineEnd: "12px",
                      },
                      ...(designC && {
                        marginBlockEnd: 0,
                        position: "relative",
                      }),
                    }}
                    {...(designC && {
                      menuPaperSx: {
                        height: "auto",
                        maxHeight: "300px",
                      },
                      renderHelperText: (error, message) =>
                        error ? (
                          <ErrorText
                            sx={{
                              position: "absolute",
                              top: "100%",
                            }}
                          >
                            {message}
                          </ErrorText>
                        ) : null,
                    })}
                  />
                  {designC ? (
                    <Controller
                      name="hasLicense"
                      control={control}
                      render={({ field }) => (
                        <RadioBox
                          designC
                          role="group"
                          aria-labelledby="license-question-label"
                        >
                          <Typography
                            id="license-question-label"
                            variant="body1"
                            fontWeight={700}
                          >
                            {locale === "he"
                              ? "האם יש לך רשיון לאוטובוס (D)?"
                              : "Do you have a bus license (D)?"}
                            <span
                              aria-hidden="true"
                              style={{ color: "#E00700" }}
                            >
                              *
                            </span>
                          </Typography>
                          <StyledRadioGroup
                            aria-labelledby="license-question-label"
                            value={
                              field.value == null
                                ? ""
                                : field.value
                                  ? "yes"
                                  : "no"
                            }
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => field.onChange(e.target.value === "yes")}
                          >
                            <StyledFormControlLabel
                              $designC
                              value="yes"
                              control={<Radio />}
                              label={locale === "he" ? "יש לי" : "I have"}
                            />
                            <StyledFormControlLabel
                              $designC
                              value="no"
                              control={<Radio />}
                              label={
                                locale === "he" ? "אין לי" : "I don't have"
                              }
                            />
                          </StyledRadioGroup>
                          {(errors as any)?.hasLicense && (
                            <ErrorText
                              sx={{
                                position: "absolute",
                                top: "100%",
                              }}
                            >
                              {(errors as any).hasLicense.message as string}
                            </ErrorText>
                          )}
                        </RadioBox>
                      )}
                    />
                  ) : (
                    <>
                      <Controller
                        name="hasLicense"
                        control={control}
                        render={({ field }) => {
                          return (
                            <StyledCheckboxRow>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    {...field}
                                    icon={<CheckboxIcon />}
                                    checkedIcon={<ActiveCheckbox />}
                                    checked={!!field.value}
                                    sx={{
                                      marginRight: "-8px",
                                      ":hover": {
                                        backgroundColor: "transparent",
                                      },
                                    }}
                                    onChange={(e) => {
                                      field.onChange(e.target.checked)
                                    }}
                                  />
                                }
                                label={
                                  <Typography variant="body2">
                                    {t("busLicense")}
                                  </Typography>
                                }
                                sx={{
                                  gap: "8px",
                                  marginLeft: 0,
                                }}
                              />
                            </StyledCheckboxRow>
                          )
                        }}
                      />
                      <MarketingConsent
                        name="complaint"
                        control={control}
                        tooltipText={tooltipText}
                      />
                    </>
                  )}
                  </>)}
                </StyledGrid>

                {designC && termsConfirmationText && (
                  <LegalTextContainer designC>
                    <StyledTypography
                      variant="body3"
                      component={"div"}
                      color={sharedColors.gray}
                      dangerouslySetInnerHTML={{
                        __html: termsConfirmationText,
                      }}
                    />
                  </LegalTextContainer>
                )}

                {designD && legalText && (
                  <LegalTextContainer designC>
                    <StyledTypography
                      variant="body3"
                      component={"div"}
                      color={sharedColors.gray}
                      dangerouslySetInnerHTML={{
                        __html: legalText,
                      }}
                    />
                  </LegalTextContainer>
                )}

                {designCD && (
                  <>
                    <div style={{ flex: 1 }} />
                  </>
                )}
                <ButtonContainer designB={designB}>
                  <Button
                    variant="primary"
                    type="submit"
                    fullWidth
                    disabled={isSubmitting}
                  >
                    {buttonText || "שליחה"}
                  </Button>
                </ButtonContainer>
                {RECAPTCHA_KEY && (
                  <ReCAPTCHA
                    ref={reCaptchaRef as LegacyRef<ReCAPTCHA>}
                    sitekey={RECAPTCHA_KEY}
                    size="invisible"
                    hl="iw"
                    style={
                      designCD
                        ? {
                            position: "absolute",
                          }
                        : {}
                    }
                    tabIndex={-1}
                  />
                )}
              </FormLayout>
            </InnerInnerLayout>
            {legalText && !designC && !designD && (
              <LegalTextContainer>
                <StyledTypography
                  variant="body3"
                  component={"div"}
                  color={sharedColors.gray}
                  style={{ maxWidth: 675, alignSelf: "center" }}
                  dangerouslySetInnerHTML={{ __html: legalText || "" }}
                />
              </LegalTextContainer>
            )}
          </>
        ) : (
          <Result
            clickHandler={resetForm}
            isFormError={isFormError}
            designC={designC}
            designD={designD}
          />
        )}
      </InnerLayout>
    </OuterLayout>
  )
}
